import {useCallback, useMemo, useState} from 'react';
import {BaseData, PageData, PageDataStore, TablePaginationConfig} from 'utils/hooks/useNewPageData';
import {NciDict} from './types';
import {NciAdministrationService} from '@services/NciAdministration/NciAdministrationService';
import {FilterConfig, SortConfig} from '@ui/NewTable/types';
import {isEqual} from 'lodash';
import {useQuery} from '@tanstack/react-query';

import {getTableGetParams} from 'utils/hooks/useNewPageData/utils';

export const useNciDictsTableData = <T extends BaseData>(selectedDict: NciDict) => {
    const [store, setStore] = useState<PageDataStore<T>>();
    const [pagination, setPagination] = useState<TablePaginationConfig>();
    const [sort, setSort] = useState<SortConfig>();
    const [filters, setFilters] = useState<FilterConfig>({});

    const params = useMemo(() => {
        return {...getTableGetParams(pagination, sort), ...filters};
    }, [pagination, sort, filters]);

    const {data, refetch} = useQuery<PageData<T>>({
        queryKey: ['NciDictStore', selectedDict?.pathCode],
        queryFn: () =>
            NciAdministrationService.getNciGroupsTableData(selectedDict.findAllUrl, params),
        enabled: !!selectedDict?.findAllUrl && !!params,
    });

    useMemo(() => {
        if (!data) return;
        setStore({
            data,
            total: data.page.totalElements,
            page: data.page.number,
            pageSize: data.page.size,
        });
    }, [data]);

    const update = useCallback(
        (newPagination: TablePaginationConfig, newSort: SortConfig, newFilters: FilterConfig) => {
            // Эта функция вызывается ПРИ изменении параметров и ПРОСТО в случаях, когда нам нужно перезапросить данные
            // Поэтому проверяем, если параметры не изменились, то перезапрашиваем данные
            if (
                isEqual(pagination, newPagination) &&
                isEqual(sort, newSort) &&
                isEqual(filters, newFilters)
            ) {
                refetch();
                return;
            }
            // Если изменились, то обновляем их и данные перезапросятся автоматически
            setPagination(newPagination);
            setSort(newSort);
            setFilters(newFilters);
        },
        [filters, pagination, refetch, sort],
    );

    return useMemo(() => [store, update] as const, [store, update]);
};
